import { createAction, createSlice } from '@reduxjs/toolkit'

import { localStorageIsAuthenticatedKey, localStorageRefreshTokenKey, localStorageTokenKey } from '../../@paco/entities/Authentication/Authentication'

import { initialAsyncReduxState } from '../@config/AsyncReduxState'
import {
    AUTH_AUTHENTICATE_FAILURE,
    AUTH_AUTHENTICATE_REQUEST,
    AUTH_AUTHENTICATE_SUCCESS,
    AUTH_FORGOT_PASSWORD_FAILURE,
    AUTH_FORGOT_PASSWORD_REQUEST,
    AUTH_FORGOT_PASSWORD_SUCCESS,
    AUTH_REQUEST,
    AUTH_SET_API_TOKEN,
    AUTH_SET_REFRESH_TOKEN,
    AUTH_SUBMIT_PASSWORD_REQUEST,
    AUTH_SUBMIT_PASSWORD_SUCCESS,
    AUTH_UNSET_TOKENS,
} from '../actionTypes'
import { AuthenticateFailureReason, AuthenticationState } from './authenticationModels'

interface AuthenticateSuccess {
    apiToken: string;
    apiRefreshToken: string;
    rememberMe: boolean;
}

export const authRequest = createAction(AUTH_REQUEST)
export const authAuthenticateRequest = createAction(AUTH_AUTHENTICATE_REQUEST)
export const authAuthenticateSuccess = createAction<AuthenticateSuccess>(AUTH_AUTHENTICATE_SUCCESS)
export const authAuthenticateFailure = createAction<AuthenticateFailureReason>(AUTH_AUTHENTICATE_FAILURE)
export const authSetApiToken = createAction<string>(AUTH_SET_API_TOKEN)
export const authSetRefreshToken = createAction<string>(AUTH_SET_REFRESH_TOKEN)
export const authUnsetTokens = createAction(AUTH_UNSET_TOKENS)
export const authForgotPasswordRequest = createAction(AUTH_FORGOT_PASSWORD_REQUEST)
export const authForgotPasswordSuccess = createAction(AUTH_FORGOT_PASSWORD_SUCCESS)
export const authForgotPasswordFailure = createAction(AUTH_FORGOT_PASSWORD_FAILURE)
export const authSubmitPasswordRequest = createAction(AUTH_SUBMIT_PASSWORD_REQUEST)
export const authSubmitPasswordSuccess = createAction(AUTH_SUBMIT_PASSWORD_SUCCESS)

const initialState: AuthenticationState = {
    ...initialAsyncReduxState,
    newPasswordSuccess: false,
    isAuthenticated: !! localStorage.getItem(localStorageTokenKey) || !! sessionStorage.getItem(localStorageTokenKey),
    apiToken: localStorage.getItem(localStorageTokenKey) || sessionStorage.getItem(localStorageTokenKey),
    apiRefreshToken: localStorage.getItem(localStorageRefreshTokenKey) || sessionStorage.getItem(localStorageRefreshTokenKey),
    authenticateFailure: null,
    errors: [],
}

const authenticationSlice = createSlice({
    name: 'authentication',
    initialState,
    reducers: {},
    extraReducers: builder => {
        builder
            .addCase(authRequest, state => ({
                ...state,
                isLoading: true,
                errors: [],
            }))
            .addCase(authAuthenticateRequest, state => ({
                ...state,
                isLoading: true,
                errors: [],
            }))
            .addCase(authAuthenticateSuccess, (state, action) => {
                const { apiToken, apiRefreshToken, rememberMe } = action.payload
                localStorage.setItem(localStorageIsAuthenticatedKey, 'true')

                // Store Token based on preference
                if (rememberMe) {
                    localStorage.setItem(localStorageTokenKey, apiToken)
                    localStorage.setItem(localStorageRefreshTokenKey, apiRefreshToken)
                } else {
                    sessionStorage.setItem(localStorageTokenKey, apiToken)
                    sessionStorage.setItem(localStorageRefreshTokenKey, apiRefreshToken)
                }

                return {
                    ...state,
                    isAuthenticated: true,
                    isLoading: false,
                    apiToken,
                    apiRefreshToken,
                    authenticateFailure: null,
                }
            })
            .addCase(authAuthenticateFailure, (state, action) => ({
                ...state,
                isAuthenticated: false,
                isLoading: false,
                authenticateFailure: action.payload,
            }))
            .addCase(authSetApiToken, (state, action) => {
                localStorage.setItem(localStorageTokenKey, action.payload)
                return {
                    ...state,
                    apiToken: action.payload,
                }
            })
            .addCase(authSetRefreshToken, (state, action) => {
                localStorage.setItem(localStorageRefreshTokenKey, action.payload)
                return {
                    ...state,
                    apiRefreshToken: action.payload,
                }
            })
            .addCase(authUnsetTokens, state => {
                localStorage.removeItem(localStorageTokenKey)
                localStorage.removeItem(localStorageRefreshTokenKey)
                localStorage.removeItem(localStorageIsAuthenticatedKey)

                sessionStorage.removeItem(localStorageTokenKey)
                sessionStorage.removeItem(localStorageRefreshTokenKey)

                return {
                    ...state,
                    apiToken: null,
                    apiRefreshToken: null,
                    isAuthenticated: false,
                }
            })
            .addCase(authForgotPasswordRequest, state => ({
                ...state,
                isLoading: true,
            }))
            .addCase(authForgotPasswordSuccess, state => ({
                ...state,
                isLoading: false,
            }))
            .addCase(authForgotPasswordFailure, state => ({
                ...state,
                isLoading: false,
            }))
            .addCase(authSubmitPasswordRequest, state => ({
                ...state,
                isLoading: true,
            }))
            .addCase(authSubmitPasswordSuccess, state => ({
                ...state,
                isLoading: false,
                newPasswordSuccess: true,
            }))
    },
})

export default authenticationSlice.reducer
